import type { CSSProperties } from 'react'
import type { BillData } from '../interfaces/exportBill'

// Landscape A4 at 96 DPI = 1123 × 794px
const A4_LANDSCAPE_W = 1123
// Rows per page: header ~50px, table header ~30px, footer ~25px, row ~22px
// Available: 794 - 32 - 50 - 30 - 25 ≈ 657px → ~29 rows
const ROWS_PER_PAGE = 23

interface ReportByRowTemplateProps {
  bills: BillData[]
  yearMonth: string
}

const s: Record<string, CSSProperties> = {
  page: {
    fontFamily: "'NotoSansThai', 'Noto Sans Thai', 'Sarabun', sans-serif",
    fontSize: 11,
    color: '#333',
    padding: 16,
    width: A4_LANDSCAPE_W - 32,
    boxSizing: 'border-box',
    background: '#fff',
  },
  header: {
    textAlign: 'center',
    marginBottom: 10,
  },
  title: {
    fontWeight: 'bold',
    fontSize: 14,
    margin: '0 0 2px 0',
  },
  subtitle: {
    fontSize: 12,
    margin: 0,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    marginTop: 10,
  },
  th: {
    border: '1px solid #000',
    padding: '5px 6px',
    textAlign: 'center',
    fontWeight: 'bold',
    fontSize: 10,
    background: '#f0f0f0',
    verticalAlign: 'middle',
  },
  td: {
    border: '1px solid #000',
    padding: '4px 6px',
    verticalAlign: 'middle',
    fontSize: 10,
  },
  tdRight: {
    border: '1px solid #000',
    padding: '4px 6px',
    verticalAlign: 'middle',
    textAlign: 'right',
    fontSize: 10,
  },
  tdCenter: {
    border: '1px solid #000',
    padding: '4px 6px',
    verticalAlign: 'middle',
    textAlign: 'center',
    fontSize: 10,
  },
  tdTotal: {
    border: '1px solid #000',
    padding: '4px 6px',
    verticalAlign: 'middle',
    textAlign: 'right',
    fontWeight: 'bold',
    fontSize: 10,
    color: '#c00',
  },
  summaryRow: {
    border: '1px solid #000',
    padding: '5px 6px',
    textAlign: 'right',
    fontWeight: 'bold',
    fontSize: 11,
    background: '#f0f0f0',
  },
  footer: {
    marginTop: 8,
    fontSize: 10,
    color: '#555',
    textAlign: 'right',
  },
  pageBreak: {
    breakAfter: 'page' as const,
    pageBreakAfter: 'always' as const,
  },
  pageNumber: {
    marginTop: 4,
    fontSize: 9,
    color: '#888',
    textAlign: 'center',
  },
}

const fmt = (n: number) => Number(n || 0).toLocaleString()

function formatMonthLabel(yearMonth: string): string {
  const [year, month] = yearMonth.split('-')
  const d = new Date(Number(year), Number(month) - 1, 1)
  return d.toLocaleDateString('th-TH', { month: 'long', year: 'numeric' })
}

export function ReportByRowTemplate({
  bills,
  yearMonth,
}: ReportByRowTemplateProps) {
  const grandTotal = bills.reduce((sum, b) => sum + b.total, 0)
  const colSums = {
    electricTotal: bills.reduce((s, b) => s + b.electricTotal, 0),
    waterTotal: bills.reduce((s, b) => s + b.waterTotal, 0),
    rent: bills.reduce((s, b) => s + b.rent, 0),
    maintenanceFee: bills.reduce((s, b) => s + b.maintenanceFee, 0),
    internet: bills.reduce((s, b) => s + b.internet, 0),
    furniture: bills.reduce((s, b) => s + b.furniture, 0),
    fee: bills.reduce((s, b) => s + b.fee, 0),
  }
  const monthLabel = formatMonthLabel(yearMonth)

  const pages: BillData[][] = []
  for (let i = 0; i < bills.length; i += ROWS_PER_PAGE) {
    pages.push(bills.slice(i, i + ROWS_PER_PAGE))
  }
  if (pages.length === 0) pages.push([])

  const totalPages = pages.length
  const printDate = new Date().toLocaleDateString('th-TH', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })

  const tableHeader = (
    <thead>
      <tr>
        <th style={{ ...s.th, width: '3%' }}>ลำดับ</th>
        <th style={{ ...s.th, width: '5%' }}>เลขห้อง</th>
        <th style={{ ...s.th, width: '10%' }}>ชื่อผู้เช่า</th>
        <th style={{ ...s.th, width: '5%' }}>ไฟเก่า</th>
        <th style={{ ...s.th, width: '5%' }}>ไฟใหม่</th>
        <th style={{ ...s.th, width: '6%' }}>ค่าไฟ</th>
        <th style={{ ...s.th, width: '5%' }}>น้ำเก่า</th>
        <th style={{ ...s.th, width: '5%' }}>น้ำใหม่</th>
        <th style={{ ...s.th, width: '6%' }}>ค่าน้ำ</th>
        <th style={{ ...s.th, width: '7%' }}>ค่าเช่า</th>
        <th style={{ ...s.th, width: '7%' }}>ส่วนกลาง</th>
        <th style={{ ...s.th, width: '7%' }}>เน็ต</th>
        <th style={{ ...s.th, width: '7%' }}>เฟอร์ฯ</th>
        <th style={{ ...s.th, width: '7%' }}>อื่นๆ</th>
        <th style={{ ...s.th, width: '7%' }}>รวม</th>
        <th style={{ ...s.th, width: '5%' }}>สถานะ</th>
      </tr>
    </thead>
  )

  return (
    <>
      {pages.map((pageBills, pageIndex) => {
        const isLastPage = pageIndex === totalPages - 1
        const globalOffset = pageIndex * ROWS_PER_PAGE
        const pageKey = `page-${pageIndex}`

        return (
          <div
            key={pageKey}
            style={isLastPage ? s.page : { ...s.page, ...s.pageBreak }}
          >
            {/* Header */}
            <div style={s.header}>
              <p style={s.title}>สุขกมล อพาร์ทเม้นท์</p>
              <p style={s.subtitle}>รายงานประจำเดือน : {monthLabel}</p>
            </div>

            {/* Table */}
            <table style={s.table}>
              {tableHeader}
              <tbody>
                {pageBills.map((bill, index) => {
                  const tenantName =
                    [bill.tenantFirstName, bill.tenantLastName]
                      .filter(Boolean)
                      .join(' ') || '-'
                  const isPaid = bill.paymentStatus === 'paid'
                  return (
                    <tr key={bill.recordId}>
                      <td style={s.tdCenter}>{globalOffset + index + 1}</td>
                      <td style={s.tdCenter}>{bill.roomNumber}</td>
                      <td style={s.td}>{tenantName}</td>
                      <td style={s.tdRight}>{fmt(bill.elecPrev)}</td>
                      <td style={s.tdRight}>{fmt(bill.elecNow)}</td>
                      <td style={s.tdRight}>{fmt(bill.electricTotal)}</td>
                      <td style={s.tdRight}>{fmt(bill.waterPrev)}</td>
                      <td style={s.tdRight}>{fmt(bill.waterNow)}</td>
                      <td style={s.tdRight}>{fmt(bill.waterTotal)}</td>
                      <td style={s.tdRight}>{fmt(bill.rent)}</td>
                      <td style={s.tdRight}>{fmt(bill.maintenanceFee)}</td>
                      <td style={s.tdRight}>{fmt(bill.internet)}</td>
                      <td style={s.tdRight}>{fmt(bill.furniture)}</td>
                      <td style={s.tdRight}>{fmt(bill.fee)}</td>
                      <td style={s.tdTotal}>{fmt(bill.total)}</td>
                      <td
                        style={{
                          ...s.tdCenter,
                          color: isPaid ? '#16a34a' : '#c00',
                          fontWeight: 'bold',
                        }}
                      >
                        {isPaid ? 'ชำระแล้ว' : 'ค้างชำระ'}
                      </td>
                    </tr>
                  )
                })}

                {/* Column subtotal row on last page only */}
                {isLastPage && (
                  <tr>
                    <td
                      colSpan={3}
                      style={{ ...s.summaryRow, textAlign: 'right' }}
                    >
                      รวม
                    </td>
                    <td style={s.summaryRow} />
                    <td style={s.summaryRow} />
                    <td style={{ ...s.summaryRow, color: '#000' }}>
                      {fmt(colSums.electricTotal)}
                    </td>
                    <td style={s.summaryRow} />
                    <td style={s.summaryRow} />
                    <td style={{ ...s.summaryRow, color: '#000' }}>
                      {fmt(colSums.waterTotal)}
                    </td>
                    <td style={{ ...s.summaryRow, color: '#000' }}>
                      {fmt(colSums.rent)}
                    </td>
                    <td style={{ ...s.summaryRow, color: '#000' }}>
                      {fmt(colSums.maintenanceFee)}
                    </td>
                    <td style={{ ...s.summaryRow, color: '#000' }}>
                      {fmt(colSums.internet)}
                    </td>
                    <td style={{ ...s.summaryRow, color: '#000' }}>
                      {fmt(colSums.furniture)}
                    </td>
                    <td style={{ ...s.summaryRow, color: '#000' }}>
                      {fmt(colSums.fee)}
                    </td>
                    <td style={{ ...s.summaryRow, color: '#c00' }}>
                      {fmt(grandTotal)}
                    </td>
                    <td style={s.summaryRow} />
                  </tr>
                )}
                {/* Grand total row on last page only */}
                {isLastPage && (
                  <tr>
                    <td
                      colSpan={14}
                      style={{ ...s.summaryRow, textAlign: 'right' }}
                    >
                      รวมทั้งหมด ({bills.length} ห้อง)
                    </td>
                    <td style={{ ...s.summaryRow, color: '#c00' }}>
                      {fmt(grandTotal)}
                    </td>
                    <td style={s.summaryRow} />
                  </tr>
                )}
              </tbody>
            </table>

            {/* Footer */}
            <div style={s.footer}>
              {isLastPage && <>พิมพ์วันที่ {printDate} &nbsp;|&nbsp; </>}
              หน้า {pageIndex + 1} / {totalPages}
            </div>
          </div>
        )
      })}
    </>
  )
}
